import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, IconButton } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import { type PartnerListItemDto, PartnerStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { EntityStatusChip } from "Shared/Components/EntityStatusChip";
import { DetailActionCell } from "Shared/DataTable/DetailActionCell";
import { EnterIcon } from "Shared/Icons/EnterIcon";
import { NameLinkCell } from "Shared/NameLinkCell";
import { getFormattedValue, phoneMask } from "Shared/Utils/Format/object";

export type FlatPartner = PartnerListItemDto & {
  parentPartnerID: string | null;
};

const columnHelper = createColumnHelper<FlatPartner>();

export const PARTNERS_SORTABLE_COLUMNS = [
  "companyName",
  "projectCount",
  "email",
  "phone",
] as const;

const isSortable = (id: string) =>
  (PARTNERS_SORTABLE_COLUMNS as readonly string[]).includes(id);

export function usePartnersTableColumns(
  expandedPartners: Set<string>,
  togglePartnerExpansion: (id: string) => void,
  hasSubPartners: (id: string) => boolean,
) {
  const { t } = useResource();

  return useMemo(
    () => [
      columnHelper.accessor("companyName", {
        header: t(Resources.Partners.PartnerName),
        enableSorting: isSortable("companyName"),
        cell: (info) => {
          const partner = info.row.original;
          const isParent = partner.parentPartnerID === null;
          const isExpanded = expandedPartners.has(partner.partnerID);
          const hasChildren = hasSubPartners(partner.partnerID);

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                paddingLeft: isParent ? 0 : 4,
              }}
            >
              {isParent && hasChildren && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePartnerExpansion(partner.partnerID);
                  }}
                  sx={{
                    padding: 0.5,
                    color: "white",
                    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" },
                  }}
                >
                  {isExpanded ? (
                    <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              )}
              {isParent && !hasChildren && <Box sx={{ width: 28 }} />}
              {!isParent && (
                <Box sx={{ ml: 4, mr: 1 }}>
                  <EnterIcon />
                </Box>
              )}
              <NameLinkCell
                path={AppRouting.PartnerDetail.path(partner.partnerID)}
              >
                <Box
                  sx={{
                    opacity: isParent ? 1 : 0.8,
                    fontWeight: isParent ? 500 : 400,
                  }}
                >
                  {info.getValue()}
                </Box>
              </NameLinkCell>
            </Box>
          );
        },
      }),
      columnHelper.accessor("projectCount", {
        header: t(Resources.Partners.Projects),
        enableSorting: isSortable("projectCount"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("email", {
        header: t(Resources.Partners.Email),
        enableSorting: isSortable("email"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("phone", {
        header: t(Resources.Partners.Phone),
        enableSorting: isSortable("phone"),
        cell: (info) =>
          getFormattedValue(info.getValue(), undefined, phoneMask),
      }),
      columnHelper.accessor("status", {
        header: t(Resources.Common.Status),
        enableSorting: false,
        cell: (info) => (
          <EntityStatusChip
            isActive={info.getValue() === PartnerStatus.Active}
            size="small"
          />
        ),
      }),
      columnHelper.display({
        id: "action",
        header: "",
        enableSorting: false,
        meta: { maxWidth: 50, justifySelf: "end" },
        cell: (info) => (
          <DetailActionCell
            path={AppRouting.PartnerDetail.path(info.row.original.partnerID)}
          />
        ),
      }),
    ],
    [t, expandedPartners, togglePartnerExpansion, hasSubPartners],
  );
}
